import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useSEO } from '../utils/seo';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import { translateMessage } from '../i18n/apiErrors';
const API = (process.env.REACT_APP_BACKEND_URL || '');

const COPY = {
  fr: {
    seoTitle: 'Réinitialiser le mot de passe — Le Placard à Doudous', seoDescription: 'Définissez un nouveau mot de passe.',
    mismatch: 'Les mots de passe ne correspondent pas', tooShort: 'Minimum 8 caractères', done: 'Mot de passe réinitialisé !', error: 'Erreur',
    invalid: 'Lien invalide', newLink: 'Demander un nouveau lien', subtitle: 'Choisir un nouveau mot de passe', newPassword: 'Nouveau mot de passe',
    hidePassword: 'Masquer le mot de passe', showPassword: 'Afficher le mot de passe', confirm: 'Confirmer', loading: 'Mise à jour...', submit: 'Enregistrer le nouveau mot de passe',
  },
  en: {
    seoTitle: 'Reset password — Le Placard à Doudous', seoDescription: 'Choose a new password.',
    mismatch: 'The passwords don’t match', tooShort: '8 characters minimum', done: 'Password reset!', error: 'Something went wrong',
    invalid: 'Invalid link', newLink: 'Request a new link', subtitle: 'Choose a new password', newPassword: 'New password',
    hidePassword: 'Hide password', showPassword: 'Show password', confirm: 'Confirm', loading: 'Updating...', submit: 'Save the new password',
  },
};

export default function ResetPassword() {
  const c = useCopy(COPY);
  const { locale, lp } = useLocale();
  useSEO(c.seoTitle, c.seoDescription);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error(c.mismatch); return; }
    if (form.password.length < 8) { toast.error(c.tooShort); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, new_password: form.password }) });
      const data = await res.json();
      if (res.ok) { toast.success(c.done); navigate(lp('/connexion')); }
      else toast.error(translateMessage(typeof data.detail === 'string' ? data.detail : '', locale) || c.error);
    } catch { toast.error(c.error); }
    setLoading(false);
  };

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="font-serif text-xl text-foreground mb-4">{c.invalid}</p>
        <Button asChild variant="outline" className="rounded-xl font-sans">
<Link to={lp('/mot-de-passe-oublie')}>{c.newLink}</Link>
</Button>
      </div>
    </div>
  );

  return (
    <div className="hero-mist noise min-h-screen flex items-center justify-center pt-20 pb-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md mx-4">
        <div className="rounded-3xl bg-card p-8 shadow-ambient sm:p-10">
          <div className="text-center mb-8">
            <Link to={lp('/')} className="font-serif text-2xl text-foreground">Le Placard à Doudous</Link>
            <p className="font-sans text-sm text-[hsl(var(--muted-foreground))] mt-2">{c.subtitle}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="reset-password" className="font-sans text-sm">{c.newPassword}</Label>
              <div className="relative">
                <Input id="reset-password" autoComplete="new-password" minLength={8} type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} required className="rounded-xl font-sans pr-10" placeholder="Minimum 8 caractères" />
                <button type="button" aria-label={showPwd ? c.hidePassword : c.showPassword} aria-pressed={showPwd} onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-confirm" className="font-sans text-sm">{c.confirm}</Label>
              <Input id="reset-confirm" autoComplete="new-password" minLength={8} type="password" value={form.confirm} onChange={e => setForm(p => ({...p, confirm: e.target.value}))} required className="rounded-xl font-sans" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary text-primary-foreground font-sans font-medium shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-ambient)] transition-[box-shadow] duration-200">
              {loading ? c.loading : c.submit}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
